/* WonderCases — Viewport-based auto play/pause (media-level, IO + debounce)
   - HTML5 <video>, YouTube, Vimeo
   - PLAY only when: isIntersecting === true AND visibility >= ENTER_THRESHOLD for >= DEBOUNCE_MS
   - PAUSE when: visibility <= EXIT_THRESHOLD OR isIntersecting === false (for >= DEBOUNCE_MS)
   - Starts paused on load (no offscreen autoplay) — provider oldali autoplay letiltva
   - Respects manual pause lock on BOTH container and media:
       container.dataset.wcUserPaused === '1' OR mediaEl.dataset.wcUserPaused === '1' => never auto-play
   - Emits: document 'wc:auto-state' { detail: { el, playing: boolean } } on state flips (for icon sync)
*/
(function(){
  // --- tuning ---
  const ENTER_THRESHOLD = 0.30;   // >= 30% => candidate to PLAY
  const EXIT_THRESHOLD  = 0.15;   // <= 15% => candidate to PAUSE
  const DEBOUNCE_MS     = 200;    // must hold beyond threshold for this long

  const CONTAINER_SEL = [
    '.video-hero', '.video-hero__media-container', '.video-hero__video-wrapper',
    '.slideshow__slide', '.slideshow__media', 'section[data-section-id]'
  ].join(', ');

  // -------- helpers --------
  const isEl = (n,t)=> n && n.tagName === t;

  function addOrUpdateQueryParam(url, key, val){
    try{ const u = new URL(url, location.href); u.searchParams.set(key,val); return u.toString(); }
    catch(_){
      const hasQ = url.includes('?'); const re=new RegExp('([?&])'+key+'=[^&]*');
      if (re.test(url)) return url.replace(re,'$1'+key+'='+encodeURIComponent(val));
      return url+(hasQ?'&':'?')+key+'='+encodeURIComponent(val);
    }
  }

  function detectType(node){
    if (!node) return null;
    if (isEl(node,'VIDEO'))  return { type:'html5', el: node };
    if (isEl(node,'IFRAME')){
      const src = (node.getAttribute('src')||'').toLowerCase();
      if (src.includes('youtube.com') || src.includes('youtu.be')){
        node.setAttribute('src', addOrUpdateQueryParam(node.getAttribute('src')||'', 'enablejsapi', '1'));
        return { type:'youtube', el: node };
      }
      if (src.includes('vimeo.com')) return { type:'vimeo', el: node };
    }
    return null;
  }

  function closestContainer(fromEl){
    return (fromEl && fromEl.closest(CONTAINER_SEL)) || document.body;
  }

  function wcIsUserPaused(container, mediaEl){
    const c = !!(container && container.dataset && container.dataset.wcUserPaused === '1');
    const m = !!(mediaEl && mediaEl.dataset && mediaEl.dataset.wcUserPaused === '1');
    return c || m;
  }

  // visible ratio (0..1) of element within viewport
  function visibilityRatio(el){
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth  || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (r.width <= 0 || r.height <= 0) return 0;
    if (r.bottom <= 0 || r.top >= vh || r.right <= 0 || r.left >= vw) return 0;
    const x1 = Math.max(0, r.left),  y1 = Math.max(0, r.top);
    const x2 = Math.min(vw, r.right),y2 = Math.min(vh, r.bottom);
    const inter = Math.max(0, x2-x1) * Math.max(0, y2-y1);
    return inter / (r.width * r.height);
  }

  function elementIntersecting(el){
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth  || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return !(r.bottom <= 0 || r.top >= vh || r.right <= 0 || r.left >= vw);
  }

  // --- provider prep: disable autoplay + policy-friendly attrs ---
  function ensurePrepared(m){
    if (!m || m.__prepped) return;
    m.__prepped = true;

    if (m.type === 'html5'){
      m.el.autoplay = false;
      m.el.removeAttribute('autoplay');
      m.el.setAttribute('playsinline','');
      m.el.setAttribute('webkit-playsinline','');
      if (m.el.muted !== true) m.el.muted = true;
    } else if (m.type === 'youtube' || m.type === 'vimeo'){
      const curAllow = (m.el.getAttribute('allow') || '').toLowerCase();
      const merged = new Set(curAllow.split(';').map(s=>s.trim()).filter(Boolean).concat(['autoplay','encrypted-media','picture-in-picture']));
      m.el.setAttribute('allow', Array.from(merged).join('; '));
      const src0 = m.el.getAttribute('src') || '';
      let src1 = addOrUpdateQueryParam(src0, 'autoplay', '0');
      src1 = addOrUpdateQueryParam(src1, 'muted', '1');
      src1 = addOrUpdateQueryParam(src1, 'playsinline', '1');
      if (m.type === 'youtube') src1 = addOrUpdateQueryParam(src1, 'enablejsapi', '1');
      if (src1 !== src0) m.el.setAttribute('src', src1);
    }
  }

  function playMedia(m){
    if (!m) return;
    if (m.type === 'html5'){
      const v = m.el;
      v.setAttribute('playsinline',''); v.setAttribute('webkit-playsinline','');
      if (v.muted !== true) v.muted = true;
      const p = v.play(); if (p && p.catch) p.catch(()=>{});
    } else if (m.type === 'youtube'){
      m.el.contentWindow && m.el.contentWindow.postMessage(JSON.stringify({event:'command', func:'playVideo', args:[]}), '*');
    } else if (m.type === 'vimeo'){
      m.el.contentWindow && m.el.contentWindow.postMessage(JSON.stringify({method:'play'}), '*');
    }
  }

  function pauseMedia(m){
    if (!m) return;
    if (m.type === 'html5'){ m.el.pause(); }
    else if (m.type === 'youtube'){
      m.el.contentWindow && m.el.contentWindow.postMessage(JSON.stringify({event:'command', func:'pauseVideo', args:[]}), '*');
    } else if (m.type === 'vimeo'){
      m.el.contentWindow && m.el.contentWindow.postMessage(JSON.stringify({method:'pause'}), '*');
    }
  }

  // -------- media registry --------
  const REG = new Set(); // {el, media, container, inView, timer, lastRatio, lastIntersect}

  function findMediaIn(container){
    const direct = Array.from(container.children).find(n => isEl(n,'VIDEO') || isEl(n,'IFRAME'));
    if (direct) return detectType(direct);
    const v = container.querySelector('video');  if (v) return detectType(v);
    const f = container.querySelector('iframe'); if (f) return detectType(f);
    return null;
  }

  function attachObserver(el){ io.observe(el); }

  function flip(row, nextIn){
    // Respect manual pause lock when trying to play
    if (nextIn && wcIsUserPaused(row.container, row.el)) return;
    row.inView = nextIn;
    if (nextIn) {
      playMedia(row.media);
      document.dispatchEvent(new CustomEvent('wc:auto-state', { detail: { el: row.el, playing: true }}));
    } else {
      pauseMedia(row.media);
      document.dispatchEvent(new CustomEvent('wc:auto-state', { detail: { el: row.el, playing: false }}));
    }
  }

  function schedule(row, targetIn){
    if (row.timer) clearTimeout(row.timer);
    row.timer = setTimeout(()=>{
      // Re-check with fresh geometry to avoid races
      const ratio = visibilityRatio(row.el);
      const intersecting = elementIntersecting(row.el);
      if (targetIn){
        if (intersecting && ratio >= ENTER_THRESHOLD && !row.inView){
          flip(row, true);
        }
      } else {
        if ((!intersecting || ratio <= EXIT_THRESHOLD) && row.inView){
          flip(row, false);
        }
      }
      row.timer = null;
    }, DEBOUNCE_MS);
  }

  // -------- IntersectionObserver per media element --------
  const io = new IntersectionObserver((entries)=>{
    entries.forEach(entry=>{
      const row = [...REG].find(x => x.el === entry.target);
      if (!row) return;
      row.lastIntersect = entry.isIntersecting;
      row.lastRatio = entry.intersectionRatio;

      // Decide desired target state based on *current* measurement:
      // - Candidate PLAY only if intersecting && ratio >= ENTER_THRESHOLD
      // - Candidate PAUSE if !intersecting OR ratio <= EXIT_THRESHOLD
      if (!row.inView && entry.isIntersecting && entry.intersectionRatio >= ENTER_THRESHOLD){
        schedule(row, true);
      } else if (row.inView && (!entry.isIntersecting || entry.intersectionRatio <= EXIT_THRESHOLD)){
        schedule(row, false);
      }
      // else within hysteresis band: do nothing
    });
  }, { root:null, rootMargin:'0px', threshold:[0, 0.15, 0.3, 0.5, 1] });

  function track(container){
    const media = findMediaIn(container);
    if (!media) return;
    const el = media.el;
    if ([...REG].some(e=>e.el===el)) return;

    // seed flags
    if (!container.dataset.wcUserPaused) container.dataset.wcUserPaused = '0';
    if (!el.dataset.wcUserPaused)        el.dataset.wcUserPaused        = '0';

    // prepare providers (disable autoplay etc.)
    ensurePrepared(media);

    // hard-stop on load if not truly in-view (prevents offscreen autoplay)
    const inter = elementIntersecting(el);
    const ratio = visibilityRatio(el);
    if (!(inter && ratio >= ENTER_THRESHOLD)) pauseMedia(media);

    // register & observe
    REG.add({ el, media, container, inView:false, timer:null, lastRatio:0, lastIntersect:false });
    attachObserver(el);

    // safety re-pause taps for iframes that might sneak-start before JS API is ready
    if (media.type === 'youtube' || media.type === 'vimeo'){
      setTimeout(()=>{ const ii = elementIntersecting(el), rr = visibilityRatio(el);
        if (!(ii && rr >= ENTER_THRESHOLD)) pauseMedia(media);
      }, 0);
      setTimeout(()=>{ const ii = elementIntersecting(el), rr = visibilityRatio(el);
        if (!(ii && rr >= ENTER_THRESHOLD)) pauseMedia(media);
      }, 400);
    }
  }

  function observeAll(ctx){
    (ctx||document).querySelectorAll(CONTAINER_SEL).forEach(track);
  }

  // Pause everything when tab hidden; resume governed by IO when visible
  document.addEventListener('visibilitychange', ()=>{
    if (document.hidden){
      REG.forEach(row => pauseMedia(row.media));
    }
  });

  // Optional: if toggle announces manual play & already inView, play now
  document.addEventListener('wc:user-play', (e)=>{
    const c = e.detail && e.detail.container; const mEl = e.detail && e.detail.mediaEl;
    if (!c) return;
    const row = [...REG].find(x => x.container === c || x.el === mEl);
    if (row && row.inView) {
      playMedia(row.media);
      document.dispatchEvent(new CustomEvent('wc:auto-state', { detail: { el: row.el, playing: true }}));
    }
  });

  // bootstrap
  document.addEventListener('DOMContentLoaded', ()=>observeAll(document));
  document.addEventListener('shopify:section:load',  e=>observeAll(e.target));
  document.addEventListener('shopify:section:select',e=>observeAll(e.target));
})();
